import { publish } from 'rxjs/operators';
import { GenericAlertButton, GenericAlertData } from '../dialogs';
import { escapeHtml } from './escapeHtml';
import { VisualDto, VisualUsageDto } from 'src/app/services/api.services';

export interface DeleteFormattingOptions {
  shared: boolean;
  scheduled: boolean;
  published: boolean;
  inAnyDashboard?: boolean;
  inSharedDashboard?: boolean;
  inScheduledDashboard?: boolean;
  inPublishedDashboard?: boolean;
  action?: string;
  wrapNameInStrongTag?: boolean;
};

export function getDeleteDialogDataVisualAndUsage(visual: VisualDto, usage: VisualUsageDto) {
  let itemType = visual.sample ? 'Sample Visual' : 'Visual';
  let actionName = visual.sample ? 'remove' : 'delete';

  let options: DeleteFormattingOptions = {
    shared: visual.shared,
    scheduled: usage.inSchedule,
    published: usage.published,
    inAnyDashboard: usage.inDashboard,
    inSharedDashboard: usage.inSharedDashboard,
    inScheduledDashboard: usage.inScheduledDashboard,
    inPublishedDashboard: usage.inPublishedDashboard,
    action: actionName,
    wrapNameInStrongTag: true
  };

  return getDeleteDialogData(itemType, visual.name, options);
}

export function getDeleteDialogData(
  itemType: string,
  itemName: string,
  options: DeleteFormattingOptions) {
  return <GenericAlertData>{
    title: 'Alert',
    content: formatDeleteDialogMarkup(itemType, itemName, options),
    buttons: [GenericAlertButton.cancel, GenericAlertButton.delete]
  };
}

export function formatDeleteDialogMarkup(
  itemType: string,
  itemName: string,
  options: DeleteFormattingOptions)
{
  let deleteMessage = formatDeleteMessage(itemType, options);

  let message: string[] = [];
 
  if (deleteMessage.warning) {
    message.push(deleteMessage.warning);
    message.push(`<p><strong>${escapeHtml(itemName)}</strong></p>`);
  }
  else {
    message.push('<p>', deleteMessage.confirmation, '</p>');
    message.push(`<p><strong>${escapeHtml(itemName)}</strong></p>`);
  }
  
  if (deleteMessage.showBottomConfirm) {
    message.push(`<p>Are you sure you want to delete?</p>`);
  }

  return message.join('');
}

export function formatDeleteMessage(
  itemType: string,
  options: DeleteFormattingOptions)
{
  let warning = formatDeleteWarning(itemType, options);

  return {
    warning,
    showBottomConfirm: !!warning,
    confirmation:
      `Are you sure you want to ${options.action ?? 'delete'} the following <strong>${itemType}</strong>?`
  };
}

export function formatDeleteWarning(
  itemType: string,
  options: DeleteFormattingOptions) {
  let isInNonSharedDashboard = !!options.inAnyDashboard && (!options.inSharedDashboard && !options.inScheduledDashboard && !options.inPublishedDashboard);
  let shared = options.shared || !!options.inSharedDashboard;
  let scheduled = options.scheduled || !!options.inScheduledDashboard;
  let published = options.published || !!options.inPublishedDashboard;

  if (!shared && !scheduled && !published && !isInNonSharedDashboard)
    return null;

  let tagL = options.wrapNameInStrongTag ? '<strong>' : '';
  let tagR = options.wrapNameInStrongTag ? '</strong>' : '';

  let markup = [
    '<div>',
    `<p>The following ${tagL}${itemType}${tagR} is currently</p>`,
    '<div>'
  ];

  if (options.shared) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-share-alt"></i>Shared</div>`);
  }
  else if (options.inSharedDashboard) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-share-alt"></i>Shared in Dashboard</div>`);
  }

  if (options.scheduled) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-calendar-check"></i>Scheduled</div>`);
  }
  else if (options.inScheduledDashboard) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-calendar-check"></i>Scheduled in Dashboard</div>`);
  }

  if (options.published) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-user-chart"></i>Published</div>`);
  }
  else if (options.inPublishedDashboard) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-user-chart"></i>Published in Dashboard</div>`);
  }

  if (isInNonSharedDashboard) {
    markup.push(`<div class="delete-msg-badge"><i class="fas fa-th-large"></i>Used in Dashboard<div>`)
  }
  markup.push(
    '</div>',
    '</div>');

  return markup.join('');
}

